import React, { forwardRef } from 'react';
import clsx from 'clsx';

/**
 * Form controls: text inputs, text areas, selects and checkboxes.
 *
 * Each renders the native element with every prop passed through. `tone`
 * supplies the colours (border, focus ring, background); size and spacing come
 * from `className`, so every field keeps its own look while its colours come
 * from this one list. All colours are theme tokens. For a field with a label,
 * helper text and icons, use `Input` or `Select`.
 */

export const FIELD_TONES = {
  /** Light grey border. */
  default: 'border-gray-200',
  /** Slightly darker grey border. */
  strong: 'border-gray-300',
  /** The app's own border colour. */
  agentic: 'border-agentic-gray-300',
  /** Light border with a blue focus state. */
  'focus-blue': 'border-gray-200 focus:border-blue-500 focus:ring-blue-100',
  /** Primary-coloured focus ring. */
  'focus-primary': 'focus:border-transparent focus:ring-agentic-primary-blue',
  /** Light border with a primary-coloured focus ring. */
  'default-focus-primary': 'border-gray-200 focus:border-transparent focus:ring-agentic-primary-blue',
  /** White field with the app's border and a brand-coloured focus border. */
  'agentic-focus-brand': 'bg-white border-agentic-gray-300 focus:border-kv-agentic-blue',
  /** Grey filled background. */
  muted: 'bg-gray-100',
  /** No background. */
  transparent: 'bg-transparent',
  /** No colours of its own: all colours come from `className`. */
  custom: '',
} as const;

export type FieldTone = keyof typeof FIELD_TONES;

const toneClass = (tone: FieldTone | undefined, className?: string) =>
  clsx(FIELD_TONES[tone ?? 'custom'], className) || undefined;

export type TextFieldProps = React.InputHTMLAttributes<HTMLInputElement> & { tone?: FieldTone };

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({ tone, className, ...props }, ref) => (
  <input ref={ref} className={toneClass(tone, className)} {...props} />
));
TextField.displayName = 'TextField';

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & { tone?: FieldTone };

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({ tone, className, ...props }, ref) => (
  <textarea ref={ref} className={toneClass(tone, className)} {...props} />
));
TextArea.displayName = 'TextArea';

export type SelectFieldProps = React.SelectHTMLAttributes<HTMLSelectElement> & { tone?: FieldTone };

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(({ tone, className, ...props }, ref) => (
  <select ref={ref} className={toneClass(tone, className)} {...props} />
));
SelectField.displayName = 'SelectField';

export type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { tone?: FieldTone };

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({ tone, className, ...props }, ref) => (
  <input ref={ref} type="checkbox" className={toneClass(tone, className)} {...props} />
));
Checkbox.displayName = 'Checkbox';
